import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { api } from '../services/api';
import { colors } from '../styles';
import Logger from '../utils/logger';

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: '8px 24px',
  backgroundColor: colors.primary,
  color: colors.white,
  '&:hover': {
    backgroundColor: colors.primary,
    opacity: 0.9
  },
  '&:disabled': {
    backgroundColor: `${colors.primary}50`,
    color: colors.white
  }
}));

/**
 * Plaid update mode link trigger.
 * - Fetches an update-mode link token for a given Item
 * - Opens Plaid Link in update mode
 * - Does not exchange public_token (update mode keeps existing access_token)
 */
export default function PlaidUpdateLink({
  itemId,
  accountSelectionEnabled = false,
  onResolved,
  buttonProps = {},
  children,
  autoOpen = false,
}) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openWhenReady, setOpenWhenReady] = useState(false);
  // Link token we already auto-opened. `open` changes identity whenever the parent
  // re-renders with a new onResolved, so without this a user who cancels Link
  // would have it pop straight back open.
  const autoOpenedTokenRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        const response = await api.post('/plaid/create_update_link_token', {
          itemId,
          accountSelectionEnabled: !!accountSelectionEnabled
        });
        if (!isMounted) return;
        setToken(response.data.link_token);
      } catch (err) {
        console.error('Error creating update link token:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [itemId, accountSelectionEnabled]);

  const config = useMemo(() => ({
    token,
    onSuccess: (public_token, metadata) => {
      Logger.debug('[Plaid] Update mode success', {
        link_session_id: metadata?.link_session_id,
        item_id: itemId,
        institution_id: metadata?.institution?.institution_id
      });
      
      // Success in update mode; no exchange needed
      onResolved?.();
      // Optionally resolve prompts immediately; webhook fallback still applies
      api.post('/plaid/resolve_update_prompt', { itemId }).catch((e) => {
        Logger.error('[Plaid] Error resolving update prompt:', e);
      });
    },
    onExit: (err, metadata) => {
      Logger.debug('[Plaid] Update mode exit', {
        link_session_id: metadata?.link_session_id,
        item_id: itemId,
        error_type: err?.error_type,
        error_code: err?.error_code,
        status: metadata?.status
      });
      
      if (err) {
        Logger.error('[Plaid] Update mode exit error:', err);
      }
    },
    onEvent: (eventName, metadata) => {
      Logger.debug('[Plaid] Update mode event', {
        event_name: eventName,
        link_session_id: metadata?.link_session_id,
        item_id: itemId,
        view_name: metadata?.view_name,
        error_type: metadata?.error_type,
        error_code: metadata?.error_code
      });
    }
  }), [token, onResolved, itemId]);

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (openWhenReady && ready) {
      open();
      setOpenWhenReady(false);
    }
  }, [openWhenReady, ready, open]);

  useEffect(() => {
    if (autoOpen && ready && token && autoOpenedTokenRef.current !== token) {
      autoOpenedTokenRef.current = token;
      open();
    }
  }, [autoOpen, ready, token, open]);

  if (autoOpen) {
    return loading ? <CircularProgress size={20} /> : null;
  }

  if (loading) {
    return <CircularProgress size={20} />;
  }

  return (
    <StyledButton
      variant="contained"
      onClick={() => {
        if (ready) {
          open();
        } else {
          setOpenWhenReady(true);
        }
      }}
      disabled={!token || !ready}
      {...buttonProps}
    >
      {children || 'Update connection'}
    </StyledButton>
  );
}


